/**
 * UC4 — Aurora DSQL for the RULES layer, and the integrity that costs.
 *
 * ── THE ENGINE ASSERTION IS THE WHOLE PANEL ─────────────────────────────────────────────────
 *
 * Every /v4 response carries `engine: "aurora-dsql"`, and the routes REFUSE (503
 * DSQL_UNAVAILABLE) rather than falling back to Aurora PostgreSQL when DSQL is unreachable. So a
 * result rendered under a DSQL heading cannot have come from a different engine — that is
 * structural, not a matter of care. This panel asserts the field on every one of its three calls
 * and treats a missing or different value as an ERROR rather than something to decorate: an
 * instrument that cannot prove it engaged must invalidate the claim, not dress it up.
 *
 * There is deliberately NO local-render fallback here, unlike UC1/UC2. UC1's local mode is honest
 * because it demonstrates barcode CONSTRUCTION, which is pure arithmetic the browser really can
 * do. UC4's entire claim is that a different engine resolved the rules, and a JavaScript stand-in
 * would render a barcode under a heading naming a database that was never involved.
 *
 * ── WHY THE CUSTOMER IS A DROPDOWN AND THE CONSTRUCT IS NOT ─────────────────────────────────
 *
 * UC1 offers a construct picker because `POST /v1/serials` genuinely takes one: there the
 * construct is an input. Here it is the RESOLVED OUTPUT of the rule join, so a construct dropdown
 * would let a viewer override the very engine the tab exists to demonstrate. Changing the CUSTOMER
 * moves the construct honestly, because the data moved rather than the caller asking:
 *
 *     acme    / PRIORITY → C03  (STC 123, rule acme_priority_light_nsa)
 *     acme    / GROUND   → C04  (STC 031, rule ground_light_default)
 *     globex  / PRIORITY → C07  (STC 777, rule globex_priority_compact)
 *     initech / INTL     → C01  (STC 042, rule initech_intl_medium, needs a 9-digit ZIP+4)
 *
 * `hooli` (SUSPENDED) and `FIRSTCLASS` (RETIRED) are offered on purpose. They fail, and the
 * failure is the point: it comes from the DATA via the engine, not from client-side validation.
 *
 * ── THE INTEGRITY TABLE IS FETCHED, NOT TRANSCRIBED ─────────────────────────────────────────
 *
 * `GET /v4/integrity` returns the guarantees list from the running system, and there is no local
 * copy to fall back on — two tables that can disagree are worse than either alone, because a
 * viewer cannot tell which one the deployed system believes. The count is cross-checked rather
 * than trusted: the response's own `movedGuarantees` is compared against the rows that actually
 * came back, because an overstated integrity cost gets an architecture rejected for a reason that
 * is not true.
 */

import { useEffect, useRef, useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Link from '@cloudscape-design/components/link';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SerialCard from './SerialCard.jsx';
import RawJson from './RawJson.jsx';
import { barcodeFromApiItem } from '../lib/impb.js';
import { timedFetch, errorText, isPlaceholder } from '../lib/api.js';
import {
  CUSTOMER_OPTIONS,
  SERVICE_OPTIONS,
  OBSERVED_RESOLUTIONS,
  DEFAULT_CUSTOMER,
  DEFAULT_SERVICE,
  ZIP_DESCRIPTION,
  zipProblem,
  parseWeightOz,
  bandForOz,
} from '../lib/rules-catalog.js';
import {
  C05_C06_UNREACHABLE,
  LATENCY_CLIENT_SIDE,
  UC4_FK3_RESIDUAL,
  UC4_R8_READ_NOT_MEASURED,
} from '../lib/caveats.js';

/**
 * `enforcedBy` as the API reports it, mapped to a badge. Three categories, because the third one
 * exists to prevent a miscount: triggers did not MOVE anywhere, they are a mechanism that no
 * longer exists, and filing them as "application" inflated the integrity cost from 6 to 7. An
 * overstated cost gets an option rejected for a reason that is not true.
 *
 * An unrecognised value is rendered VERBATIM in a grey badge rather than dropped — if the server
 * grows a fourth category, the honest failure is showing an unfamiliar word, not silently filing
 * it under one of these three.
 */
const ENFORCED_BADGE = {
  application: { color: 'red', text: 'moved to Lambda' },
  database: { color: 'green', text: 'still in the database' },
  nothing: { color: 'grey', text: 'mechanism gone' },
};

/** Per-requirement verdicts. Only R7/R8 and the integrity ones; the rest live in the doc. */
const REQUIREMENTS = [
  {
    id: 'R7',
    text: 'Active-active multi-region at all layers',
    verdict: 'MET, proven live',
    tone: 'green',
    detail:
      'Two DSQL clusters, each naming the other as a peer, witness us-east-2. Verified bidirectionally: a row written in us-east-1 is immediately readable in us-west-2, AND us-west-2 accepts writes that us-east-1 then sees — with no primary between them. Aurora Global Database narrows this gap without closing it: global write forwarding lets a secondary cluster accept writes, but it forwards each one to the single primary to execute, so it buys a write ENDPOINT in every region rather than a WRITER. Lose the primary region and writes stop until a failover promotes a new one, and DDL is not forwarded at all. So the discriminating check is not whether a second region can take a write — forwarding gives it that — but whether it can still take one with the other region gone. Write-forwarding behaviour is READ from AWS documentation, not measured here.',
  },
  {
    id: 'R8',
    text: 'Zero outages, not even for patching',
    verdict: 'READ from AWS docs, NOT measured here',
    tone: 'blue',
    detail: UC4_R8_READ_NOT_MEASURED,
  },
  {
    id: 'R2',
    text: '120-day uniqueness per MID/STC',
    verdict: 'unchanged by UC4',
    tone: 'grey',
    detail:
      'Serial vending is untouched by this option. Uniqueness still comes from the disjoint range partition plus the DynamoDB high-water mark, and the 120-day rotation is the reclamation mechanism in UC1 — not anything DSQL does.',
  },
];

/** Constraints found by applying the schema to a live cluster, absent from the docs we read. */
const SCHEMA_FINDINGS = [
  {
    what: 'Identity columns must be BIGINT',
    detail:
      'INTEGER GENERATED ALWAYS AS IDENTITY — what our Aurora schema uses and Aurora accepts — is rejected: "datatype integer not supported, identity column type must be bigint". Not cosmetic: widening a parent identity key forces every foreign-key column referencing it to widen too.',
  },
  {
    what: 'CREATE INDEX must be CREATE INDEX ASYNC',
    detail:
      'Plain CREATE INDEX is rejected: "unsupported mode. please use CREATE INDEX ASYNC." This is R8’s mechanism made syntax — DSQL can promise uninterrupted schema changes partly BECAUSE it refuses the blocking form, inverting PostgreSQL where blocking CREATE INDEX is the default. Cost: the statement returns before the index is usable.',
  },
];

/**
 * `mode` is the colour mode, threaded through to SerialCard because the barcode canvas is drawn
 * with raw pixels rather than design tokens, so it cannot pick the colours up from a theme.
 */
export default function Uc4Panel({ mode, endpoints, live, liveProbe }) {
  const [customer, setCustomer] = useState(DEFAULT_CUSTOMER);
  const [service, setService] = useState(DEFAULT_SERVICE);
  const [weightOz, setWeightOz] = useState('12');
  const [destZip, setDestZip] = useState('20260');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // The rules browser (GET /v4/rules) and the integrity view (GET /v4/integrity) each keep their
  // own busy/error/result, so a failure in one never blanks the other. They are separate claims.
  const [rulesBusy, setRulesBusy] = useState(false);
  const [rulesError, setRulesError] = useState(null);
  const [rules, setRules] = useState(null);

  const [integrityBusy, setIntegrityBusy] = useState(false);
  const [integrityError, setIntegrityError] = useState(null);
  const [integrity, setIntegrity] = useState(null);

  /**
   * The engine assertion, in ONE place so all three /v4 calls are held to it identically.
   *
   * A response that does not report `engine: "aurora-dsql"` is a FAILURE, not a result to
   * decorate — the whole claim of this tab is which engine answered. Amendment A6 (buffer bypass)
   * and A13 (the DAX read) applied to the engine, and INVARIANT #10: an instrument that cannot
   * prove it engaged must invalidate the claim rather than dress it up.
   */
  function assertDsql(payload) {
    if (!payload || payload.engine !== 'aurora-dsql') {
      throw new Error(
        `the response did not report engine "aurora-dsql" (got ${JSON.stringify(
          payload ? payload.engine : undefined
        )}), so this did NOT prove DSQL answered — refusing to present it as a DSQL result`
      );
    }
  }

  /**
   * Live-only, for the same reason the vend is: see vendViaDsql(). A local stand-in for a rules
   * READ would be a table of rows under a DSQL heading with no DSQL involved.
   */
  function requireLive(what) {
    if (!live) {
      throw new Error(
        `${what} needs the live backend: UC4's claim is that Aurora DSQL answered, and a local stand-in could not demonstrate that.` +
          (liveProbe && liveProbe.reason ? ` ${liveProbe.reason}` : '')
      );
    }
  }

  /**
   * Vend through DSQL. LIVE-ONLY, DELIBERATELY — there is no local-render fallback here, unlike
   * UC1/UC2.
   *
   * UC1's local mode is honest because it demonstrates barcode CONSTRUCTION, which is pure
   * arithmetic the browser really can do. UC4's entire claim is that a DIFFERENT ENGINE resolved
   * the rules. A JavaScript stand-in would render a barcode under a heading that says Aurora DSQL
   * while no DSQL was involved — the exact false-pass shape amendment A6 guards on the buffer
   * bypass and A13 on the DAX read path. So when the backend is unreachable this refuses to
   * pretend rather than showing an illustrative result.
   *
   * And it ASSERTS the engine on the way back: a response that does not report
   * engine === 'aurora-dsql' is treated as a failure, not decorated. That is the instrument
   * proving it engaged (INVARIANT #10), and it is the same guard the uc4-dsql load scenario uses
   * to invalidate its own run.
   */
  async function vendViaDsql() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      requireLive('The DSQL vend');
      const zipBad = zipProblem(destZip);
      if (zipBad) throw new Error(zipBad);
      const w = parseWeightOz(weightOz);
      if (w.error) throw new Error(w.error);

      const res = await timedFetch(
        `${endpoints.rulesApi}/v4/labels`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            customerId: customer.value,
            serviceCode: service.value,
            destZip: destZip.trim(),
            weightOz: w.oz,
          }),
        },
        20000
      );
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      assertDsql(payload);
      let barcode = null;
      let barcodeError = null;
      if (payload.label && payload.label.pic) {
        try {
          barcode = barcodeFromApiItem(payload.label);
        } catch (err) {
          barcodeError = err.message;
        }
      }
      setResult({ ms: res.ms, payload, barcode, barcodeError, placeholder: isPlaceholder(payload) });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * GET /v4/rules?customerId= — the DSQL READ path, and the browser for what the engine has to
   * choose between. Mirrors UC2's "show customer rule set", which is the parity this panel was
   * missing: without it a viewer can see a resolved answer but not the candidate set it came from,
   * and one row is indistinguishable from a hardcoded constant.
   *
   * It also shows the `engine` field UC2 has no equivalent for, which is the whole comparison.
   */
  async function loadRules() {
    setRulesError(null);
    setRules(null);
    setRulesBusy(true);
    try {
      requireLive('The rules browser');
      const res = await timedFetch(
        `${endpoints.rulesApi}/v4/rules?customerId=${encodeURIComponent(customer.value)}`,
        {},
        15000
      );
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      assertDsql(payload);
      setRules({ ms: res.ms, payload });
    } catch (err) {
      setRulesError(err.message);
    } finally {
      setRulesBusy(false);
    }
  }

  /**
   * GET /v4/integrity — the guarantees table, READ FROM THE RUNNING SYSTEM.
   *
   * This replaces a hand-transcribed copy of `MOVED_TO_APPLICATION`. The transcription was not
   * kept as an offline fallback, deliberately: two tables that can disagree are worse than one,
   * because a viewer has no way to tell which one the deployed system actually believes. If the
   * fetch fails, this says so — an unavailable integrity report is a knowable state, whereas a
   * stale local copy presented as current is not.
   *
   * The count is cross-checked rather than trusted: `movedGuarantees` from the response is
   * compared against the rows that actually came back with enforcedBy === 'application'. A
   * disagreement is surfaced, because an overstated integrity cost gets an option rejected for a
   * reason that is not true.
   */
  async function loadIntegrity() {
    setIntegrityError(null);
    setIntegrity(null);
    setIntegrityBusy(true);
    try {
      requireLive('The integrity report');
      const res = await timedFetch(`${endpoints.rulesApi}/v4/integrity`, {}, 15000);
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      assertDsql(payload);
      const guarantees = Array.isArray(payload.guarantees) ? payload.guarantees : [];
      if (!guarantees.length) {
        throw new Error(
          'the integrity endpoint answered but returned no guarantees — an empty list is not "nothing moved", it is a missing answer, and presenting it as a clean bill of health would invert the finding'
        );
      }
      const movedRows = guarantees.filter((g) => g.enforcedBy === 'application').length;
      const countMismatch =
        typeof payload.movedGuarantees === 'number' && payload.movedGuarantees !== movedRows
          ? { reported: payload.movedGuarantees, counted: movedRows }
          : null;
      setIntegrity({ ms: res.ms, payload, guarantees, movedRows, countMismatch });
    } catch (err) {
      setIntegrityError(err.message);
    } finally {
      setIntegrityBusy(false);
    }
  }

  /**
   * Load the integrity report ONCE, automatically, as soon as the backend is known to be live.
   *
   * This is a READ, not a vend — it allocates nothing and burns no serials, so auto-loading costs
   * nothing a manual press would save, and the table is the substance of this tab: leaving the
   * headline evidence behind a button would make the page's central claim about integrity cost
   * invisible on arrival. `scripts/verify-web-live.sh` asserts the residual-risk text renders.
   *
   * The button stays, for re-reading after a deploy. `didAutoLoad` makes this fire once rather
   * than on every `live` re-render, so a flapping probe cannot turn it into a poll.
   */
  const didAutoLoad = useRef(false);
  useEffect(() => {
    if (!live || didAutoLoad.current) return;
    didAutoLoad.current = true;
    loadIntegrity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  // What the current form selection is EXPECTED to resolve to, from observed live behaviour. Shown
  // as an expectation the round trip then confirms or refutes — never as the result itself.
  const expected = OBSERVED_RESOLUTIONS.find(
    (r) => r.customerId === customer.value && r.serviceCode === service.value
  );
  const bandNow = bandForOz(Number(weightOz));

  return (
    <SpaceBetween size="l">
      <Alert type="info" header="Aurora DSQL resolves the rules, and every response has to prove it">
        <SpaceBetween size="xs">
          <Box variant="p">
            The rules layer moves from Aurora PostgreSQL to <strong>Aurora DSQL</strong>, running
            active-active in both regions. Serial vending is unchanged. Every response reports{' '}
            <code>engine: &quot;aurora-dsql&quot;</code> and this panel refuses to render one that
            does not — and the API refuses with <code>503</code> rather than quietly falling back
            to PostgreSQL, so a result under this heading cannot have come from the other engine.
          </Box>
          <Box variant="p">
            <strong>Change the customer to change the construct.</strong> There is no construct
            picker here on purpose: the construct is the rules&apos; <em>answer</em>, so a dropdown
            would let you override the engine being demonstrated. <code>acme</code>/PRIORITY
            resolves <code>C03</code>, <code>globex</code>/PRIORITY <code>C07</code>,{' '}
            <code>initech</code>/INTL <code>C01</code> (which needs a 9-digit ZIP+4), and any
            GROUND <code>C04</code>. Four constructs, chosen by data.
          </Box>
          <Box variant="p" data-testid="uc4-c05-c06">
            {C05_C06_UNREACHABLE}
          </Box>
        </SpaceBetween>
      </Alert>

      {/*
        VEND THROUGH DSQL. Live-only by design — see vendViaDsql() for why there is no local
        fallback: a JavaScript stand-in would render a barcode under a DSQL heading with no DSQL
        involved. The response's `engine` field is asserted, so this button either proves DSQL
        resolved the rules or reports that it could not.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="POST /v4/labels — the same six-table join as UC2, resolved by Aurora DSQL instead of Aurora PostgreSQL, then the SAME UC1 serial path (DynamoDB mark + Valkey buffer)."
          >
            Vend a label through Aurora DSQL
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/*
            THE CUSTOMER IS THE CONSTRUCT CONTROL, AND THERE IS NO CONSTRUCT PICKER ON PURPOSE.
            See the file header. Changing customer moves the resolved construct across C03/C04/C07/
            C01 because the DATA moves — which is the capability UC1 exposes with a picker, shown
            here the only honest way for an architecture whose claim is that rules decide.
          */}
          <ColumnLayout columns={4}>
            <FormField
              label="Customer"
              description="the four customers seeded in the DSQL rules tables — changing this changes the RESOLVED construct"
            >
              <Select
                selectedOption={customer}
                onChange={({ detail }) => setCustomer(detail.selectedOption)}
                options={CUSTOMER_OPTIONS}
                data-testid="uc4-customer"
              />
            </FormField>
            <FormField label="Service" description="must be an ACTIVE subscription for this customer">
              <Select
                selectedOption={service}
                onChange={({ detail }) => setService(detail.selectedOption)}
                options={SERVICE_OPTIONS}
                data-testid="uc4-service"
              />
            </FormField>
            <FormField
              label="Weight (oz)"
              description={bandNow ? `selects the weight band — ${bandNow}` : 'selects the weight band'}
            >
              <Input
                value={weightOz}
                type="number"
                onChange={({ detail }) => setWeightOz(detail.value)}
                data-testid="uc4-weight"
              />
            </FormField>
            <FormField label="Destination ZIP" description={ZIP_DESCRIPTION}>
              <Input
                value={destZip}
                onChange={({ detail }) => setDestZip(detail.value)}
                data-testid="uc4-zip"
              />
            </FormField>
          </ColumnLayout>

          {/*
            An EXPECTATION, labelled as one. It is what this pair resolved to when checked against
            the deployed API, shown before the call so the viewer can see the round trip confirm or
            REFUTE it. Stating it as the result would be the assertion this whole repo avoids.
          */}
          {expected ? (
            <Box variant="small" color="text-body-secondary">
              <StatusIndicator type="pending">
                expected: rule <code>{expected.rule}</code> → STC <code>{expected.stc}</code> /
                construct <code>{expected.construct}</code> — an expectation from a previous live
                check, not a result. The vend below either confirms it or contradicts it.
              </StatusIndicator>
            </Box>
          ) : (
            <Box variant="small" color="text-body-secondary">
              <StatusIndicator type="info">
                this customer/service pair has no recorded expectation — it may be refused by the
                rules (a suspended customer, a retired service, or a missing subscription), which is
                a demonstration of enforcement rather than a bug.
              </StatusIndicator>
            </Box>
          )}

          <Box textAlign="right">
            <Button variant="primary" loading={busy} onClick={vendViaDsql} data-testid="uc4-submit">
              Vend via DSQL
            </Button>
          </Box>

          {error ? (
            <Alert
              type="error"
              header="The DSQL vend did not succeed, and is not being presented as if it had"
              data-testid="uc4-error"
            >
              {error}
            </Alert>
          ) : null}

          {result ? (
            <SpaceBetween size="s">
              <Alert
                type="success"
                header={`Resolved by Aurora DSQL in ${Math.round(result.ms)} ms`}
                data-testid="uc4-result"
              >
                <Box variant="p">
                  The response reported <code>engine: &quot;aurora-dsql&quot;</code>, which is what
                  makes this a DSQL result rather than a barcode that merely looks right. Rule{' '}
                  <strong>{result.payload.resolution ? result.payload.resolution.ruleName : 'n/a'}</strong>{' '}
                  selected STC{' '}
                  <strong>{result.payload.label ? result.payload.label.serviceTypeCode : 'n/a'}</strong>{' '}
                  / construct{' '}
                  <strong data-testid="uc4-construct">
                    {result.payload.label ? result.payload.label.construct : 'n/a'}
                  </strong>
                  . Nothing in the request named that construct — it is what the rules chose.
                </Box>
              </Alert>

              {/*
                The resolution block, which is the evidence that a JOIN produced this rather than a
                lookup: it names the customer tier, the weight band the ounces landed in, the
                mailer ID the customer row supplied, and whether the winning rule was
                customer-specific. Those come from four different tables.
              */}
              {result.payload.resolution ? (
                <KeyValuePairs
                  columns={4}
                  items={Object.entries(result.payload.resolution).map(([k, v]) => ({
                    label: k,
                    value: <code>{String(v)}</code>,
                  }))}
                />
              ) : null}

              {result.barcode ? (
                <SerialCard
                  barcode={result.barcode}
                  index={null}
                  mode={mode}
                  extra={{ live: true, requestId: result.payload.requestId }}
                />
              ) : result.barcodeError ? (
                <Alert type="warning" header="The barcode returned did not parse">
                  {result.barcodeError}
                </Alert>
              ) : null}
              <RawJson value={result.payload} />
            </SpaceBetween>
          ) : null}
        </SpaceBetween>
      </Container>

      {/*
        THE RULES BROWSER — GET /v4/rules?customerId=, the DSQL READ path.

        This is UC2's "show customer rule set" reached through the other engine, and it is the
        parity this panel was missing. Its job is the same discrimination argument UC2 makes: a
        resolved answer on its own is indistinguishable from a constant, so the candidate set the
        engine had to choose between has to be visible. Here that set is every rule available to the
        customer, with the customer-specific ones and their priorities shown, so the viewer can see
        WHY the winner won before ever pressing vend.

        Plus the field UC2 has no equivalent for: `engine`.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="GET /v4/rules?customerId= — the READ path against Aurora DSQL. Every rule available to the selected customer, so the winning rule can be seen beating the ones it outranked."
            actions={
              <Button loading={rulesBusy} onClick={loadRules} data-testid="uc4-rules-load">
                Load rules for {customer.value}
              </Button>
            }
          >
            Browse the rule set in DSQL
          </Header>
        }
      >
        <SpaceBetween size="m">
          {rulesError ? (
            <Alert type="error" header="The rule set could not be read from DSQL">
              {rulesError}
            </Alert>
          ) : null}

          {rules ? (
            <SpaceBetween size="s">
              <StatusIndicator type="success">
                read from Aurora DSQL in <strong>{rules.ms.toFixed(1)} ms</strong> —{' '}
                <code>engine: &quot;aurora-dsql&quot;</code> asserted. Browser-observed, one sample,
                not a p99.
              </StatusIndicator>

              {rules.payload.customer ? (
                <KeyValuePairs
                  columns={4}
                  items={Object.entries(rules.payload.customer).map(([k, v]) => ({
                    label: k,
                    value: <code>{String(v)}</code>,
                  }))}
                />
              ) : null}

              {/*
                An empty rule set is NOT "no rules exist" — for a SUSPENDED customer it is the
                engine declining to offer any. Saying which is the difference between reporting an
                enforcement outcome and reporting a blank table.
              */}
              {rules.payload.customer && rules.payload.customer.status !== 'ACTIVE' ? (
                <Alert type="warning" header={`Customer status is ${rules.payload.customer.status}`}>
                  The rule set is empty because the CUSTOMER is not ACTIVE, not because no rules are
                  defined. Vending for this customer is refused by the rules engine — that refusal
                  comes from the data, which is a demonstration of enforcement rather than a defect.
                </Alert>
              ) : null}

              <Table
                variant="embedded"
                wrapLines
                data-testid="uc4-rules-table"
                items={(rules.payload.rules || []).map((r, i) => ({ ...r, key: `${r.ruleId}-${i}` }))}
                trackBy="key"
                empty={<Box variant="p">No rules returned for this customer.</Box>}
                header={
                  <Header variant="h3" counter={`(${(rules.payload.rules || []).length})`}>
                    Rules available to {customer.value}
                  </Header>
                }
                columnDefinitions={[
                  { id: 'name', header: 'Rule', cell: (r) => <Box fontWeight="bold">{r.name}</Box>, width: 240 },
                  { id: 'serviceCode', header: 'Service', cell: (r) => <code>{r.serviceCode}</code>, width: 120 },
                  { id: 'bandCode', header: 'Band', cell: (r) => <code>{r.bandCode}</code>, width: 100 },
                  { id: 'stc', header: 'STC', cell: (r) => <code>{r.stc}</code>, width: 80 },
                  { id: 'construct', header: 'Construct', cell: (r) => <code>{r.construct}</code>, width: 110 },
                  {
                    id: 'scope',
                    header: 'Scope',
                    cell: (r) =>
                      r.customerSpecific ? (
                        <Badge color="green">customer-specific</Badge>
                      ) : (
                        <Badge color="grey">default</Badge>
                      ),
                    width: 170,
                  },
                  {
                    id: 'priority',
                    header: 'Priority',
                    cell: (r) => <code>{String(r.priority)}</code>,
                    width: 100,
                  },
                  {
                    id: 'zip',
                    header: 'ZIP gate',
                    cell: (r) => (r.destZipPrefix ? <code>prefix {r.destZipPrefix}</code> : '—'),
                    minWidth: 120,
                  },
                ]}
              />
              <RawJson value={rules.payload} />
            </SpaceBetween>
          ) : null}
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="Aurora DSQL replaces Aurora PostgreSQL for the RULES layer only. Serial vending is unchanged."
          >
            What UC4 is
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Rules store</Box>
              <Box>Aurora DSQL (was Aurora PostgreSQL)</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Serial vending</Box>
              <Box>Unchanged — DynamoDB mark + Valkey buffer</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Referential integrity</Box>
              <Box>
                <Badge color="red">application-enforced</Badge>
              </Box>
            </div>
          </ColumnLayout>

          <Alert type="warning" header="This option is viable only because of an ASSUMPTION nobody has confirmed">
            <SpaceBetween size="xs">
              <Box variant="p">
                <strong>The assumption:</strong> application-enforced referential integrity is
                acceptable on the rules layer. <strong>The customer never said this.</strong> They
                have never used the words &quot;foreign key&quot; or &quot;referential
                integrity&quot;; the only statement on record is &quot;rules for valid response
                permutations&quot;. If the assumption is wrong, UC4 is the wrong answer and the
                choice moves to CockroachDB (active-active <em>and</em> globally-enforced foreign
                keys) or Aurora Global Database (whose write forwarding gives a secondary region a
                write endpoint but still executes every write on the one primary, so it fails the
                active-active requirement as written).
              </Box>
              <Box variant="p" data-testid="uc4-fk3">
                <strong>The sharpest consequence, stated plainly:</strong> {UC4_FK3_RESIDUAL}
              </Box>
            </SpaceBetween>
          </Alert>
        </SpaceBetween>
      </Container>

      {/*
        WHAT INTEGRITY MOVED — FETCHED FROM GET /v4/integrity, NOT TRANSCRIBED. Two tables that can
        disagree are strictly worse than one, because a viewer has no way to tell which one the
        deployed system believes. If the fetch fails the panel says the report is unavailable — a
        knowable state — rather than showing a local copy that might already be wrong.

        `dsql-rules.test.js` asserts the shape and count at the SOURCE, so the guarantee that the
        numbers are right stays where it can fail a build rather than moving into the browser.
      */}
      <Container
        header={
          <Header
            variant="h2"
            counter={integrity ? `(${integrity.movedRows} moved)` : undefined}
            description="GET /v4/integrity — read from the RUNNING system, not transcribed into this page. Every row states its residual risk, because the honest question is not whether a validation function was written but what it still cannot catch."
            actions={
              <Button loading={integrityBusy} onClick={loadIntegrity} data-testid="uc4-integrity-load">
                Load integrity report
              </Button>
            }
          >
            What integrity moved out of the database
          </Header>
        }
      >
        <SpaceBetween size="m">
          {integrityBusy && !integrity ? (
            <StatusIndicator type="loading">reading the integrity report from Aurora DSQL…</StatusIndicator>
          ) : null}

          {!integrity && !integrityError && !integrityBusy ? (
            <Alert type="info" header="Not loaded — and deliberately not filled in from a local copy">
              This table is served by <code>GET /v4/integrity</code> and loads automatically when the
              backend is live. There is no local copy to substitute, on purpose: a second table that
              can drift out of step is worse than an empty one, because you could not tell which one
              the running system agrees with. Press <strong>Load integrity report</strong> to read it
              from DSQL.
            </Alert>
          ) : null}

          {integrityError ? (
            <Alert type="error" header="The integrity report is UNAVAILABLE, and is not being substituted">
              {integrityError}
              <Box variant="p" margin={{ top: 'xs' }}>
                There is deliberately nothing to fall back to. The source of truth on disk is{' '}
                <code>MOVED_TO_APPLICATION</code> in <code>services/rules-api/dsql-rules.js</code>,
                asserted by <code>dsql-rules.test.js</code> — read it there rather than trusting a
                stale render here.
              </Box>
            </Alert>
          ) : null}

          {integrity ? (
            <>
              <StatusIndicator type="success">
                read from Aurora DSQL in <strong>{integrity.ms.toFixed(1)} ms</strong> —{' '}
                <code>engine: &quot;aurora-dsql&quot;</code> asserted,{' '}
                <strong>{integrity.guarantees.length}</strong> guarantees returned,{' '}
                <strong>{integrity.movedRows}</strong> of them now enforced in Lambda.
              </StatusIndicator>

              {integrity.countMismatch ? (
                <Alert type="error" header="The endpoint disagrees with itself about how much moved">
                  It reported <code>movedGuarantees: {integrity.countMismatch.reported}</code> but
                  returned <strong>{integrity.countMismatch.counted}</strong> rows with{' '}
                  <code>enforcedBy: &quot;application&quot;</code>. Surfaced rather than smoothed
                  over: the integrity cost is the number this whole option is judged on, and an
                  overstated cost gets an architecture rejected for a reason that is not true.
                </Alert>
              ) : null}

              {/*
                wrapLines is REQUIRED here, not cosmetic. Without it the Residual risk column
                truncates mid-word, and the residual risk is the entire point of this table: the
                honest question is not whether a validation function was written but what it still
                cannot catch. A clipped risk column hides exactly the content that makes UC4's
                guarantee weaker than a database-enforced one.
              */}
              <Table
                variant="embedded"
                wrapLines
                data-testid="uc4-integrity-table"
                items={integrity.guarantees.map((g, i) => ({ ...g, key: `${g.id}-${i}` }))}
                trackBy="key"
                columnDefinitions={[
                  { id: 'id', header: 'ID', cell: (g) => <Box fontWeight="bold">{g.id}</Box>, width: 80 },
                  {
                    id: 'was',
                    header: 'Was enforced by the database as',
                    cell: (g) => (
                      <SpaceBetween size="xxs">
                        <Box>{g.was}</Box>
                        {g.constraint ? (
                          <Box variant="small" color="text-body-secondary">
                            <code>{g.constraint}</code>
                          </Box>
                        ) : null}
                      </SpaceBetween>
                    ),
                    width: 300,
                  },
                  {
                    id: 'enforcedBy',
                    header: 'Now',
                    cell: (g) => {
                      const b = ENFORCED_BADGE[g.enforcedBy];
                      return (
                        <SpaceBetween size="xxs">
                          <Badge color={b ? b.color : 'grey'}>{b ? b.text : String(g.enforcedBy)}</Badge>
                          {g.enforcedIn ? (
                            <Box variant="small" color="text-body-secondary">
                              <code>{g.enforcedIn}</code>
                            </Box>
                          ) : null}
                        </SpaceBetween>
                      );
                    },
                    width: 190,
                  },
                  { id: 'residualRisk', header: 'Residual risk', cell: (g) => g.residualRisk, minWidth: 320 },
                ]}
              />

              {/*
                The endpoint ships its own caveat in `note`. Rendering the server's words rather
                than a local paraphrase is the same rule as the table itself: one source, no drift.
              */}
              {integrity.payload.note ? (
                <Alert
                  type="error"
                  header="The part no code can fix, and the reason this is a weaker guarantee rather than an equivalent one"
                >
                  <Box variant="p">{integrity.payload.note}</Box>
                  <Box variant="small" margin={{ top: 'xs' }}>
                    Verbatim from the <code>note</code> field of <code>GET /v4/integrity</code> — the
                    running system&apos;s own words, not this page&apos;s summary of them.
                  </Box>
                </Alert>
              ) : null}

              <RawJson label="Raw /v4/integrity response" value={integrity.payload} />
            </>
          ) : null}
        </SpaceBetween>
      </Container>

      {/*
        UC4's MEASURED LATENCY LIVES ON THE LOAD TEST TAB, not here.

        It used to be transcribed into this file as three hand-typed figures. That is the failure
        this repo has fought hardest — retyping is how a number nobody measured reaches a customer,
        and a copy in a component cannot be checked against anything and goes stale the moment the
        benchmark is re-run. The Load test tab derives the same figures at BUILD TIME from the
        committed report, through a parser that fails the build when the report's shape moves.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="Measured in region on the EC2 runners at 20 rps, and reported on the Load test tab rather than transcribed here."
          >
            What UC4 measured
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box variant="p">
            Every <code>uc4-dsql</code> cell reported{' '}
            <code>engine: &quot;aurora-dsql&quot;</code>, so DSQL provably resolved the rules — the
            run invalidates itself otherwise. The p50/p99 per region are on the{' '}
            <strong>Load test</strong> tab, under <em>Latency per use case</em>.
          </Box>
          <Alert type="info" header="Two things the UC4 figures must not be read as">
            <SpaceBetween size="xs">
              <Box variant="p">
                <strong>Not an SLA figure at p99.</strong> UC4's high p99 is Aurora DSQL connection
                establishment on a cold Lambda execution environment, not steady-state query cost —
                at 20 rps there are not enough requests to amortise it, and a production deployment
                would pool connections.
              </Box>
              <Box variant="p">
                <strong>Not a UC4-versus-UC2 verdict.</strong> The two ran at different times
                against different connection states, so the p50 gap between them is not the engine
                difference.
              </Box>
              <Box variant="p" data-testid="uc4-apigee">
                {LATENCY_CLIENT_SIDE}
              </Box>
            </SpaceBetween>
          </Alert>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="Only the requirements UC4 actually changes. The full R1-R10 comparison across UC1/UC2/UC4 is in docs/UC4-DSQL.md."
          >
            Against the stated requirements
          </Header>
        }
      >
        <Table
          variant="embedded"
          items={REQUIREMENTS}
          wrapLines
          columnDefinitions={[
            { id: 'id', header: 'Req', cell: (r) => <Box fontWeight="bold">{r.id}</Box>, width: 70 },
            { id: 'text', header: 'Requirement', cell: (r) => r.text, width: 260 },
            {
              id: 'verdict',
              header: 'Verdict',
              cell: (r) => <Badge color={r.tone}>{r.verdict}</Badge>,
              width: 220,
            },
            { id: 'detail', header: 'Evidence', cell: (r) => r.detail },
          ]}
        />
      </Container>

      <ExpandableSection
        headerText="Two schema constraints found by APPLYING the schema, which no AWS doc we read mentions"
        variant="container"
      >
        <SpaceBetween size="m">
          <Box variant="p">
            Both were discovered by running <code>dsql-schema.sql</code> against a real cluster
            rather than reviewing it. They are the clearest argument for doing that: a paper
            migration plan would have shipped both and hit them in a deployment window.
          </Box>
          <Table
            variant="embedded"
            items={SCHEMA_FINDINGS}
            wrapLines
            columnDefinitions={[
              { id: 'what', header: 'Constraint', cell: (f) => <Box fontWeight="bold">{f.what}</Box>, width: 300 },
              { id: 'detail', header: 'Detail', cell: (f) => f.detail },
            ]}
          />
        </SpaceBetween>
      </ExpandableSection>

      {/*
        NO COMPLIANCE / AUTHORISATION SECTION HERE, DELIBERATELY. Nobody has documented an
        authorisation or compliance boundary for this workload — the question is outstanding with
        the customer. Naming one here would present a constraint as established and could eliminate
        a real option for a reason that is not known to be true. Adding such a section requires a
        documented customer requirement, not a plausible assumption.
      */}

      <Box variant="small">
        Sources on disk:{' '}
        <Link external href="https://github.com">
          docs/UC4-DSQL.md
        </Link>{' '}
        (full per-requirement comparison), <code>services/rules-api/dsql-rules.js</code> (the
        enforcement code and MOVED_TO_APPLICATION — served live by <code>GET /v4/integrity</code>,
        which is what the table above renders rather than a copy), <code>services/rules-api/dsql-schema.sql</code>{' '}
        (the ported schema, with every removed constraint noted in place),{' '}
        <code>scripts/apply-dsql-schema.js</code> (21 assertions against the live clusters), and{' '}
        <code>docs/ACTIVE-ACTIVE-RESEARCH.md</code> (the option space, with every vendor claim
        marked READ, INFERRED or NOT FOUND).
      </Box>
    </SpaceBetween>
  );
}
